import { useMemo, useState } from "react";
import { CheckCircle2, FlaskConical, GitBranch, Play, Plus, Search, Settings2, Table2, Trash2, XCircle } from "lucide-react";
import { useApi, usePermission, type CaseSummary, type PagedResult } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, InlineAlert, Input, NativeSelect, Textarea, saveFailureText } from "@ncp/ui";
import { CodeEditor } from "../components/CodeEditor";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { ConditionBuilder, type ConditionField } from "../features/generic/ConditionBuilder";
import { useDefinitionList } from "../features/generic/api";
import { DecisionTableEditor } from "../features/rules/DecisionTableEditor";
import { RULE_KINDS, inputSkeleton, useRuleFields, useRuleOnCase, useRuleTest, type RuleBody, type RuleCaseEvaluation, type RuleTestRun } from "../features/rules/api";

type Ctx = EditorContext<RuleBody>;
type T = (k: string, o?: Record<string, unknown>) => string;

function lint(b: RuleBody, t: T) {
  const errors: string[] = []; const warnings: string[] = [];
  if (b.table) {
    if (b.table.inputs.length === 0) errors.push(t("admin:rules.lint.inputs"));
    if (b.table.rows.length === 0) errors.push(t("admin:rules.lint.rows"));
  }
  if (b.testCases.length === 0) warnings.push(t("admin:rules.lint.noTests"));
  return { errors, warnings };
}

const fmt = (v: unknown) => (v === undefined ? "—" : typeof v === "string" ? v : JSON.stringify(v));

/** Rule designer: a decision table or an expression, its test cases (all must pass before it goes live) and a check against a real case. */
export function RuleDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<RuleBody>
      resource="rules"
      basePath="/rules"
      navLabel={t("admin:nav.rules")}
      icon={GitBranch}
      writePermission="platform.rules.write"
      docsAnchor="rules"
      help={<><p>{t("admin:rules.help.what")}</p><p>{t("admin:rules.help.table")}</p><p>{t("admin:rules.help.tests")}</p></>}
      tabs={[
        { key: "logic", label: t("admin:rules.tabs.logic"), icon: Table2 },
        { key: "tests", label: t("admin:rules.tabs.tests"), icon: FlaskConical },
        { key: "case", label: t("admin:rules.tabs.case"), icon: Search },
        { key: "settings", label: t("admin:rules.tabs.settings"), icon: Settings2 },
      ]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:rules.summary", { kind: t(`admin:rules.kinds.${b.kind}`), tests: b.testCases.length })}
      badges={(b) => <Badge variant="outline" size="sm">{b.table ? t("admin:rules.modes.table") : t("admin:rules.modes.logic")}</Badge>}
      render={(tab, ctx) => tab === "tests" ? <TestsTab ctx={ctx} /> : tab === "case" ? <CaseTab ctx={ctx} /> : tab === "settings" ? <SettingsTab ctx={ctx} /> : <LogicTab ctx={ctx} />}
    />
  );
}

function useConditionFields(serviceKey?: string | null): { fields: ReturnType<typeof useRuleFields>; condition: ConditionField[] } {
  const { locale } = useLocale();
  const fields = useRuleFields(serviceKey);
  const condition = useMemo(() => (fields.data ?? []).map((f) => ({ key: f.path, label: locale === "ar" && f.labelAr ? f.labelAr : f.label, type: f.type, options: f.options?.map((o) => ({ value: o, label: o })) })), [fields.data, locale]);
  return { fields, condition };
}

function LogicTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const { fields, condition } = useConditionFields(body.serviceKey);
  const [json, setJson] = useState(false);
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-2">
        <div><CardTitle>{body.table ? t("admin:rules.modes.table") : t("admin:rules.modes.logic")}</CardTitle><CardDescription>{body.table ? t("admin:rules.table.intro") : t("admin:rules.logic.intro")}</CardDescription></div>
        {!readOnly ? (
          <div className="flex gap-2">
            <Button data-testid="mode-table" size="sm" variant={body.table ? "gold" : "outline"} onClick={() => patch({ table: body.table ?? { inputs: [{ key: "in1", path: condition[0]?.key ?? "status", label: condition[0]?.label ?? "status", type: condition[0]?.type ?? "text" }], rows: [{ cells: {}, output: true }], default: false } })}><Table2 /> {t("admin:rules.modes.table")}</Button>
            <Button data-testid="mode-logic" size="sm" variant={body.table ? "outline" : "gold"} onClick={() => patch({ table: null })}><GitBranch /> {t("admin:rules.modes.logic")}</Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {body.table ? (
          <DecisionTableEditor table={body.table} fields={fields.data ?? []} kind={body.kind} readOnly={readOnly} onChange={(table) => patch({ table })} />
        ) : json ? (
          <CodeEditor value={JSON.stringify(body.logic, null, 2)} readOnly={readOnly} height="320px" onChange={(v) => { try { patch({ logic: JSON.parse(v) }); } catch { /* keep typing */ } }} />
        ) : (
          <ConditionBuilder testId="rule-condition" value={body.logic} fields={condition} readOnly={readOnly} onChange={(logic) => patch({ logic })} />
        )}
        {!body.table ? <Button size="sm" variant="ghost" onClick={() => setJson((j) => !j)}>{json ? t("admin:rules.logic.visual") : t("admin:rules.logic.json")}</Button> : null}
        {body.table ? <p className="text-xs text-muted-foreground">{t("admin:rules.table.compiled")}</p> : null}
      </CardContent>
    </Card>
  );
}

function TestsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const { fields } = useConditionFields(body.serviceKey);
  const test = useRuleTest();
  const [run, setRun] = useState<RuleTestRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const yesNo = body.kind !== "Computation";
  const execute = async () => {
    setError(null);
    try { setRun(await test.mutateAsync(body)); } catch (e) { setError(saveFailureText(e) || t("admin:common.error")); setRun(null); }
  };
  const addCase = async () => {
    let vars = run?.variables ?? [];
    if (!vars.length) { try { vars = (await test.mutateAsync({ ...body, testCases: [] })).variables; } catch { vars = []; } }
    patch((b) => ({ ...b, testCases: [...b.testCases, { name: t("admin:rules.tests.caseName", { n: b.testCases.length + 1 }), input: inputSkeleton(vars, fields.data ?? []), expected: yesNo ? true : 0 }] }));
  };
  const setCase = (i: number, p: Partial<RuleBody["testCases"][number]>) => patch((b) => ({ ...b, testCases: b.testCases.map((c, j) => (j === i ? { ...c, ...p } : c)) }));
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <div><CardTitle>{t("admin:rules.tests.title")}</CardTitle><CardDescription>{t("admin:rules.tests.intro")}</CardDescription></div>
          <div className="flex gap-2">
            {!readOnly ? <Button data-testid="add-test" size="sm" variant="outline" onClick={() => void addCase()}><Plus /> {t("admin:rules.tests.add")}</Button> : null}
            <Button data-testid="run-tests" size="sm" variant="gold" loading={test.isPending} disabled={body.testCases.length === 0} onClick={() => void execute()}><Play /> {t("admin:rules.tests.run")}</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
          {run ? (
            <div data-testid="test-summary">
              <InlineAlert tone={run.passed === run.total ? "success" : "danger"} title={t("admin:rules.tests.summary", { passed: run.passed, total: run.total })}>
                {run.passed === run.total ? t("admin:rules.tests.allPass") : t("admin:rules.tests.someFail")}
              </InlineAlert>
            </div>
          ) : null}
          {body.testCases.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:rules.tests.none")}</p> : null}
          {body.testCases.map((c, i) => {
            const result = run?.results[i];
            return (
              <div key={i} className="grid gap-2 rounded-xl border border-sand-200 p-3 lg:grid-cols-[1fr_2fr_auto]">
                <div className="space-y-2">
                  <Input value={c.name} disabled={readOnly} aria-label={t("admin:rules.tests.name")} onChange={(e) => setCase(i, { name: e.target.value })} />
                  <Field label={t("admin:rules.tests.expected")}>
                    {yesNo ? (
                      <NativeSelect data-testid={`test-expected-${i}`} value={c.expected === true ? "true" : "false"} disabled={readOnly} onChange={(e) => setCase(i, { expected: e.target.value === "true" })}>
                        <option value="true">{t("admin:rules.table.yes")}</option>
                        <option value="false">{t("admin:rules.table.no")}</option>
                      </NativeSelect>
                    ) : <Input value={fmt(c.expected)} disabled={readOnly} onChange={(e) => { const n = Number(e.target.value); setCase(i, { expected: e.target.value.trim() !== "" && Number.isFinite(n) ? n : e.target.value }); }} />}
                  </Field>
                  {result ? (
                    <div data-testid={`test-result-${i}`} className="flex items-center gap-1.5 text-sm">
                      {result.passed ? <CheckCircle2 className="size-4 text-emerald-600" /> : <XCircle className="size-4 text-red-600" />}
                      {result.passed ? t("admin:rules.tests.passed") : result.error ? result.error : t("admin:rules.tests.got", { value: fmt(result.actual) })}
                    </div>
                  ) : null}
                </div>
                <Field label={t("admin:rules.tests.input")}>
                  <Textarea data-testid={`test-input-${i}`} rows={5} className="font-mono text-xs" dir="ltr" disabled={readOnly} defaultValue={JSON.stringify(c.input, null, 2)}
                    onBlur={(e) => { try { setCase(i, { input: JSON.parse(e.target.value) as Record<string, unknown> }); } catch { /* keep the last valid input */ } }} />
                </Field>
                {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, testCases: b.testCases.filter((_, j) => j !== i) }))}><Trash2 /></Button> : null}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function CaseTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const api = useApi();
  const evaluate = useRuleOnCase();
  const [number, setNumber] = useState("");
  const [result, setResult] = useState<RuleCaseEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const check = async () => {
    setError(null); setResult(null);
    try {
      const page = await api.get<PagedResult<CaseSummary>>("/api/cases", { q: number.trim(), pageSize: 5 });
      const hit = page.items.find((c) => c.caseNumber.toLowerCase() === number.trim().toLowerCase()) ?? page.items[0];
      if (!hit) { setError(t("admin:rules.case.notFound")); return; }
      setResult(await evaluate.mutateAsync({ body: ctx.body, caseId: hit.id }));
    } catch (e) { setError(saveFailureText(e) || t("admin:common.error")); }
  };
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:rules.case.title")}</CardTitle><CardDescription>{t("admin:rules.case.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex max-w-lg gap-2">
          <Input data-testid="case-number" value={number} placeholder="EC-2026-000001" dir="ltr" onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && number.trim()) void check(); }} />
          <Button data-testid="case-check" variant="gold" disabled={!number.trim()} loading={evaluate.isPending} onClick={() => void check()}><Play /> {t("admin:rules.case.check")}</Button>
        </div>
        {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
        {result ? (
          <div data-testid="case-result" className="space-y-2">
            <InlineAlert tone={result.error ? "danger" : result.holds ? "success" : "warning"} title={result.error ? result.error : ctx.body.kind === "Computation" ? t("admin:rules.case.value", { number: result.caseNumber, value: fmt(result.result) }) : result.holds ? t("admin:rules.case.holds", { number: result.caseNumber }) : t("admin:rules.case.fails", { number: result.caseNumber })} />
            <table className="w-full max-w-2xl text-sm">
              <thead className="text-xs text-muted-foreground"><tr><th className="py-1 text-start font-medium">{t("admin:rules.case.field")}</th><th className="py-1 text-start font-medium">{t("admin:rules.case.valueCol")}</th></tr></thead>
              <tbody className="divide-y divide-sand-100">{Object.entries(result.values).map(([k, v]) => <tr key={k}><td className="py-1 font-mono text-xs">{k}</td><td className="py-1">{fmt(v)}</td></tr>)}</tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SettingsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const { body, patch, readOnly } = ctx;
  const services = useDefinitionList("services", {}, can("platform.services.read"));
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:rules.settings.title")}</CardTitle><CardDescription>{t("admin:rules.settings.intro")}</CardDescription></CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        <Field label={t("admin:rules.fields.kind")} hint={t(`admin:rules.kindHints.${body.kind}`)}>
          <NativeSelect data-testid="rule-kind" value={body.kind} disabled={readOnly} onChange={(e) => patch({ kind: e.target.value as RuleBody["kind"] })}>
            {RULE_KINDS.map((k) => <option key={k} value={k}>{t(`admin:rules.kinds.${k}`)}</option>)}
          </NativeSelect>
        </Field>
        <Field label={t("admin:rules.fields.service")} hint={t("admin:rules.settings.serviceHint")}>
          <NativeSelect data-testid="rule-service" value={body.serviceKey ?? ""} disabled={readOnly} onChange={(e) => patch({ serviceKey: e.target.value || null })}>
            <option value="">{t("admin:rules.platformWide")}</option>
            {services.data?.map((s) => <option key={s.key} value={s.key}>{locale === "ar" && s.nameAr ? s.nameAr : s.name}</option>)}
          </NativeSelect>
        </Field>
        <Field label={t("admin:rules.fields.explanation")} hint={t("admin:rules.settings.explanationHint")}><Textarea data-testid="rule-explanation" rows={2} value={body.explanation ?? ""} disabled={readOnly} onChange={(e) => patch({ explanation: e.target.value || null })} /></Field>
        <Field label={t("admin:rules.fields.explanationAr")}><Textarea rows={2} dir="rtl" value={body.explanationAr ?? ""} disabled={readOnly} onChange={(e) => patch({ explanationAr: e.target.value || null })} /></Field>
        <div className="sm:col-span-2 text-xs text-muted-foreground">{t("admin:rules.settings.usage", { key: `rule:${ctx.definition.key}` })}</div>
      </CardContent>
    </Card>
  );
}
